// usePasswordReset Hook
// Location: src/hooks/usePasswordReset.ts
// Purpose: Handle password reset requests and completion

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export const usePasswordReset = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Request Reset State
  const [email, setEmail] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  // Reset Password State
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Validation states
  const [errors, setErrors] = useState<{
    email?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  // Validate token on mount (for reset page)
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    setIsValidating(true);
    
    try {
      // Check if token format is valid (basic check)
      if (token.length < 32) {
        setTokenValid(false);
        toast.error(t('auth.errors.invalidResetLink') || 'Invalid reset link');
        return;
      }

      // Token validation happens on submission
      // For now, just mark as potentially valid
      setTokenValid(true);

    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setErrors(prev => ({ ...prev, email: t('auth.errors.emailRequired') }));
      return false;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors(prev => ({ ...prev, email: t('auth.errors.emailInvalid') }));
      return false;
    }

    setErrors(prev => ({ ...prev, email: undefined }));
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setErrors(prev => ({ 
        ...prev, 
        newPassword: t('auth.errors.passwordTooShort') 
      }));
      return false;
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    if (!hasUppercase || !hasNumber) {
      setErrors(prev => ({ 
        ...prev, 
        newPassword: t('auth.errors.passwordWeak') || 'Password must contain at least one uppercase letter and one number'
      }));
      return false;
    }

    setErrors(prev => ({ ...prev, newPassword: undefined }));
    return true;
  };

  const validatePasswordMatch = (): boolean => {
    if (newPassword !== confirmPassword) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: t('auth.errors.passwordMismatch') 
      }));
      return false;
    }

    setErrors(prev => ({ ...prev, confirmPassword: undefined }));
    return true;
  };

  const requestPasswordReset = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsRequesting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-request-password-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ email })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to request password reset');
      }

      // Always show success (prevent email enumeration)
      setRequestSuccess(true);
    //   toast.success(
    //     t('auth.passwordReset.emailSent') || 
    //     'If an account exists with this email, you will receive password reset instructions.'
    //   );

    } catch (error: any) {
      console.error('Password reset request error:', error);
      
      const errorMessage = error.message?.toLowerCase() || '';
      
      if (errorMessage.includes('rate limit')) {
        toast.error(
          t('auth.errors.tooManyResetRequests') || 
          'Too many reset requests. Please try again later.'
        );
      } else {
        // Still show success message to prevent email enumeration
        setRequestSuccess(true);
        // toast.success(
        //   t('auth.passwordReset.emailSent') || 
        //   'If an account exists with this email, you will receive password reset instructions.'
        // );
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const resetPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!validatePassword(newPassword) || !validatePasswordMatch()) {
      return;
    }

    if (!token) {
      toast.error(t('auth.errors.invalidResetLink') || 'Invalid reset link');
      return;
    }

    setIsResetting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            token,
            new_password: newPassword
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Password reset failed');
      }

      // Success
      toast.success(
        t('auth.passwordReset.resetSuccess') || 
        'Password reset successfully. You can now log in with your new password.'
      );

      // Redirect to login after brief delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error: any) {
      console.error('Password reset error:', error);
      
      const errorMessage = error.message?.toLowerCase() || '';
      
      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        toast.error(
          t('auth.errors.resetLinkExpired') || 
          'Reset link has expired. Please request a new one.'
        );
        setTokenValid(false);
      } else if (errorMessage.includes('already used')) {
        toast.error(
          t('auth.errors.resetLinkUsed') || 
          'This reset link has already been used. Please request a new one.'
        );
        setTokenValid(false);
      } else {
        toast.error(
          error.message || 
          t('auth.errors.resetFailed') || 
          'Password reset failed. Please try again.'
        );
      }
    } finally {
      setIsResetting(false);
    }
  };

  return {
    // Request Reset
    email,
    setEmail,
    isRequesting,
    requestSuccess,
    requestPasswordReset,

    // Reset Password
    token,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    isResetting,
    tokenValid,
    isValidating,
    resetPassword,

    // Validation
    errors,
    validateEmail,
    validatePassword,
    validatePasswordMatch
  };
};